import axios, {AxiosError} from 'axios';
import {useQuery} from 'react-query';
import {GATEWAY_URL_REPORT} from '../../../../../Config/Constants';
import {Storage} from '../../../../../Utils/StorageUtils';
import {
    ManagementOverviewPageState,
    ManagementOverviewRequestParams,
    ManagementOverviewResponse,
} from '../types/ManagementOverviewReport.types';
import {
    buildManagementOverviewQueryKey,
    validateManagementOverviewParams,
} from '../utils/managementOverviewRoute';

function readPersistedValue(key: string): string | null {
    const value = Storage.local.get(key) || Storage.session.get(key);
    if (typeof value !== 'string' || value.trim().length === 0) {
        return null;
    }
    return value;
}

export function buildManagementOverviewRequestHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};
    const token = readPersistedValue('api-token-key');
    const email = readPersistedValue('user-mail');
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }
    if (email) {
        headers.Email = email;
    }
    return headers;
}

export class ManagementOverviewNoContentError extends Error {
    constructor() {
        super('NO_CONTENT');
        this.name = 'ManagementOverviewNoContentError';
        Object.setPrototypeOf(this, ManagementOverviewNoContentError.prototype);
    }
}

export function isManagementOverviewNoContentError(error: unknown): boolean {
    if (error instanceof ManagementOverviewNoContentError) {
        return true;
    }
    return error instanceof Error && error.name === 'ManagementOverviewNoContentError';
}

async function fetchManagementOverviewReport(
    params: ManagementOverviewRequestParams
): Promise<ManagementOverviewResponse> {
    const response = await axios.post<ManagementOverviewResponse>(
        'api/management-data/overview',
        null,
        {
            baseURL: GATEWAY_URL_REPORT,
            params: {
                powerStationIds: params.powerStationIds.join(','),
                endDateTime: params.endDateTime,
            },
            headers: buildManagementOverviewRequestHeaders(),
            timeout: 120000,
            validateStatus: (status) => status === 200 || status === 204,
        }
    );

    if (response.status === 204) {
        throw new ManagementOverviewNoContentError();
    }

    return response.data;
}

export function resolveManagementOverviewPageState(
    validationValid: boolean,
    isLoading: boolean,
    isError: boolean,
    error: unknown
): ManagementOverviewPageState {
    if (!validationValid) {
        return 'erro';
    }
    if (isLoading) {
        return 'carregando';
    }
    if (isManagementOverviewNoContentError(error)) {
        return 'sem-dados';
    }
    if (isError) {
        return 'erro';
    }
    return 'sucesso';
}

export function useManagementOverviewReport(rawParams: Partial<ManagementOverviewRequestParams>) {
    const validation = validateManagementOverviewParams(rawParams);
    const params = validation.valid ? validation.params : null;

    const query = useQuery(
        params ? buildManagementOverviewQueryKey(params) : ['management-overview-report', 'invalid'],
        () => fetchManagementOverviewReport(params as ManagementOverviewRequestParams),
        {
            enabled: validation.valid,
            retry: false,
            refetchOnWindowFocus: false,
        }
    );

    const pageState = resolveManagementOverviewPageState(
        validation.valid,
        query.isLoading || query.isFetching,
        query.isError,
        query.error
    );

    return {
        pageState,
        data: query.data,
        params,
        validationValid: validation.valid,
        error: query.error as AxiosError | ManagementOverviewNoContentError | null,
        refetch: query.refetch,
    };
}
